import { Link } from "expo-router";
import { Camera, ClipboardCheck, Gift, MessageCircle, QrCode, Users } from "../../../components/lucide-icons";
import { StyleSheet, Text, View } from "react-native";

import { Badge, Button, Panel } from "../../../components/ui";
import { palette, spacing } from "../../../theme/tokens";
import {
  DemoSection,
  Fact,
  MemorialHubPreviewPanel,
  MonarchDemoShell,
  NextActionBar,
} from "../components/arrangement-demo-components";
import { demoArrangementCase } from "../data/demo-arrangement-case";
import { arrangementRoutes } from "../routes";
import { buildMemorialHubPreview } from "../services/mock-arrangement-ai";

export default function MemorialHubPreviewScreen() {
  const preview = buildMemorialHubPreview(demoArrangementCase);

  return (
    <MonarchDemoShell activeLabel="Memorial hub">
      <DemoSection
        label="Memorial hub"
        title="Maria Elena Hernandez"
        body="This is where the obituary belongs: as the public output of the arrangement case, alongside guestbook, QR folder, RSVP/check-in, keepsakes, and CRM capture."
      />

      <MemorialHubPreviewPanel preview={preview} />

      <View style={styles.grid}>
        <Panel style={styles.panel}>
          <View style={styles.inlineHeader}>
            <QrCode color={palette.gold} size={20} strokeWidth={1.8} />
            <Text style={styles.panelTitle}>QR digital service folder</Text>
          </View>
          <Text style={styles.panelBody}>
            Folder status: {preview.digitalFolderStatus}. Guests can scan at the service, open from
            the memorial hub, or receive a private family link from Monarch.
          </Text>
        </Panel>

        <Panel style={styles.panel}>
          <View style={styles.inlineHeader}>
            <MessageCircle color={palette.forest} size={20} strokeWidth={1.8} />
            <Text style={styles.panelTitle}>Guestbook capture</Text>
          </View>
          <Text style={styles.panelBody}>
            Public condolences, private family messages, and photo contributions are framed as
            Monarch-owned memorial engagement, not third-party buttons.
          </Text>
        </Panel>

        <Panel style={styles.panel}>
          <View style={styles.inlineHeader}>
            <Gift color={palette.cedar} size={20} strokeWidth={1.8} />
            <Text style={styles.panelTitle}>Keepsake requests</Text>
          </View>
          <Text style={styles.panelBody}>
            Guests can request memorial trees, sympathy gifts, and keepsake copies without real
            payment online. Director pricing confirmation remains the next step.
          </Text>
        </Panel>
      </View>

      <Panel spotlight style={styles.crmPanel}>
        <View style={styles.crmCopy}>
          <Badge label="Follow-up engine" tone="forest" />
          <Text style={styles.crmTitle}>The guestbook becomes a warm audience for Monarch.</Text>
          <Text style={styles.crmBody}>
            Each submission can carry relationship, message/photo status, consent, memorial association,
            and follow-up eligibility. That is the money-making layer beyond publishing the obituary.
          </Text>
        </View>
        <View style={styles.factGrid}>
          <Fact icon={Users} label="Guest source" value="Guestbook and RSVP" />
          <Fact icon={Camera} label="Media" value="Photo contribution signals" />
          <Fact icon={Gift} label="Revenue" value="Keepsake requests" />
          <Fact icon={ClipboardCheck} label="Ops" value="Thank-you follow-up" />
        </View>
        <NextActionBar>
          <Link href={arrangementRoutes.directorCase(demoArrangementCase.id) as never} asChild>
            <Button label="Back to Case Review" />
          </Link>
          <Link href={arrangementRoutes.start as never} asChild>
            <Button label="Start New Arrangement" variant="secondary" />
          </Link>
        </NextActionBar>
      </Panel>
    </MonarchDemoShell>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  panel: {
    flex: 1,
    minWidth: 280,
    gap: 10,
  },
  inlineHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
  },
  panelTitle: {
    color: palette.ink,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 15,
    lineHeight: 20,
  },
  panelBody: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
  crmPanel: {
    gap: spacing.md,
  },
  crmCopy: {
    gap: 8,
  },
  crmTitle: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 36,
    lineHeight: 38,
  },
  crmBody: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    lineHeight: 23,
  },
  factGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
});
